import { Button, Typography, Space, Table, Segmented, Switch, Tooltip, message } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, DeleteOutlined, AimOutlined, UndoOutlined, ClearOutlined } from '@ant-design/icons';
import { useMeasureStore, type MeasureEntity, type MeasureMode } from '../../stores/measureStore';

const { Text } = Typography;

const MODE_LABEL: Record<string, string> = {
  distance: '距离',
  angle: '角度',
  area: '面积',
  boxSize: '物体尺寸',
};

const MODE_ITEMS = [
  { key: 'distance', label: '两点距离' },
  { key: 'angle', label: '三点角度' },
  { key: 'area', label: '闭合面积' },
  { key: 'boxSize', label: '物体尺寸' },
  { key: 'none', label: '退出测量' },
];

function formatResult(m: MeasureEntity, unit: 'm' | 'mm') {
  const s = unit === 'mm' ? 1000 : 1;
  if (m.mode === 'distance') {
    return `斜距${((m.result.slant ?? 0) * s).toFixed(2)}${unit} 水平${((m.result.horizontal ?? 0) * s).toFixed(2)}${unit} 高差${((m.result.vertical ?? 0) * s).toFixed(2)}${unit}`;
  }
  if (m.mode === 'angle') return `${(m.result.angle ?? 0).toFixed(1)}°`;
  if (m.mode === 'area') return `${((m.result.area ?? 0) * (s === 1000 ? 1e6 : 1)).toFixed(2)} ${unit}²`;
  return `长${(m.result.width ?? 0).toFixed(2)}×宽${(m.result.depth ?? 0).toFixed(2)}×高${(m.result.height ?? 0).toFixed(2)} ${unit}`;
}

// 右侧面板：测量工具（模式/吸附/单位/撤销/清空）+ 测量记录列表
export function MeasurementTool() {
  const {
    currentMode,
    setMode,
    snapEnable,
    toggleSnap,
    unit,
    setUnit,
    tempPoints,
    undoTempPoint,
    clearTempPoints,
    list,
    removeMeasure,
    toggleMeasureVisible,
    clearAllMeasure,
  } = useMeasureStore();

  const handleModeSelect = (key: string) => {
    setMode(key as MeasureMode);
    if (key === 'none') {
      message.info('已退出测量');
    } else {
      message.info(`开始${MODE_LABEL[key]}测量：点击场景${key === 'boxSize' ? '目标模型' : '地面/模型表面'}打点`);
    }
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'mode',
      width: 70,
      render: (v: string) => MODE_LABEL[v] || v,
    },
    {
      title: '结果',
      dataIndex: 'result',
      render: (_: unknown, record: MeasureEntity) => (
        <Text style={{ fontSize: 11, color: '#d0d0d0' }}>{formatResult(record, unit)}</Text>
      ),
    },
    {
      title: '操作',
      width: 60,
      render: (_: unknown, record: MeasureEntity) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={record.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => toggleMeasureVisible(record.id)}
            style={{ color: '#888', padding: 0, width: 18, height: 18 }}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeMeasure(record.id)}
            style={{ padding: 0, width: 18, height: 18 }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 12 }}>
      {/* 测量工具区 */}
      <Text strong style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>测量工具</Text>
      <Space orientation="vertical" style={{ width: '100%' }} size={6}>
        <Space wrap size={6}>
          {MODE_ITEMS.map((item) => (
            <Button
              key={item.key}
              size="small"
              type={currentMode === item.key ? 'primary' : 'default'}
              onClick={() => handleModeSelect(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </Space>

        {currentMode && currentMode !== 'none' && (
          <Space wrap size={8} style={{ width: '100%' }}>
            <Space size={4}>
              <Text style={{ fontSize: 11, color: '#999' }}>吸附</Text>
              <Switch size="small" checked={snapEnable} onChange={toggleSnap} />
            </Space>
            <Segmented
              size="small"
              value={unit}
              onChange={(v) => setUnit(v as 'm' | 'mm')}
              options={[{ value: 'm', label: 'm' }, { value: 'mm', label: 'mm' }]}
            />
            <Tooltip title="撤销点">
              <Button size="small" icon={<UndoOutlined />} disabled={tempPoints.length === 0} onClick={undoTempPoint} />
            </Tooltip>
            <Tooltip title="取消绘制">
              <Button size="small" icon={<ClearOutlined />} disabled={tempPoints.length === 0} onClick={clearTempPoints} />
            </Tooltip>
            <Tooltip title="清空全部测量">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={clearAllMeasure} />
            </Tooltip>
            {currentMode && (
              <Text style={{ fontSize: 11, color: '#1890ff' }}>
                <AimOutlined /> 已打点 {tempPoints.length} 个
              </Text>
            )}
            {currentMode === 'area' && tempPoints.length >= 3 && (
              <Text style={{ fontSize: 11, color: '#fa8c16' }}>双击/右键闭合面积</Text>
            )}
          </Space>
        )}
      </Space>

      <div style={{ height: 1, background: '#30475e', margin: '12px 0' }} />

      {/* 测量记录区 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12, color: '#888' }}>测量记录</Text>
        <Segmented
          size="small"
          value={unit}
          onChange={(v) => setUnit(v as 'm' | 'mm')}
          options={[{ value: 'm', label: 'm' }, { value: 'mm', label: 'mm' }]}
        />
      </div>

      <Table
        dataSource={list}
        rowKey="id"
        columns={columns}
        size="small"
        pagination={false}
        locale={{ emptyText: '暂无测量数据' }}
      />

      {list.length > 0 && (
        <Button
          type="text"
          size="small"
          danger
          block
          style={{ marginTop: 8 }}
          onClick={clearAllMeasure}
        >
          清空全部测量
        </Button>
      )}
    </div>
  );
}

export default MeasurementTool;